import React, { useState } from 'react'
import PropTypes from 'prop-types'
import orderBy from 'lodash/orderBy'

import { Box, Collapse, Divider, IconButton, List, ListItem, ListItemIcon, ListItemText, Skeleton, Tooltip } from '@mui/material'
import { get } from 'utils/lodash'

import T from 'T'
import HHSectionPlaceholder from 'components/common/HHSectionPlaceholder'
import { groupBy } from 'lodash'
import ExpandMore from '@mui/icons-material/ExpandMore'
import ChevronRight from '@mui/icons-material/ChevronRight'
import { shallowEqual, useSelector } from 'react-redux'
import AddEditBeneficialOwnerDialog from 'components/customer-details/content/beneficial_owner_contacts/AddEditBeneficialOwnerDialog'
import BeneficialOwnersActionMenu from 'components/customer-details/content/beneficial_owner_contacts/BeneficialOwnersActionMenu'
import ContactView from './ContactView'
import { getContactState } from './utils'
import ContactStatusAvatar from '../../../common/avatar/ContactStatusAvatar'

const ContactsListing = ({
  contacts = [],
  loading = false,
  showActionMenu = false,
  noContactMessage = T.NO_BENEFICIAL_OWNER_CONTACTS,
  bulkExpansion,
}) => {
  const customerDetails = useSelector(state => get(state, 'CustomersReducer.customerDetails'), shallowEqual)
  const [isOpenAddEditDialog, setIsOpenAddEditDialog] = useState(false)
  const [contactsAnchorEl, setContactsAnchorEl] = useState(null)
  const [selectedContact, setSelectedContact] = useState(null)
  const [isOpenDeactivatedList, setIsOpenDeactivatedList] = useState(false)
  const shimmersArray = Array.from({ length: 5 }).fill(0)
  const allContacts = orderBy(contacts, ['active', 'firstName', 'lastName'])
  const groupedContacts = groupBy(allContacts, ({ active }) => !active)
  const activeContacts = get(groupedContacts, 'false', [])
  const deactivatedContacts = get(groupedContacts, 'true', [])
  const accountId = get(customerDetails, 'id')

  const handleOpenActionMenu = event => {
    event.stopPropagation()
    setContactsAnchorEl(event.currentTarget)
  }

  const handleCloseActionMenu = () => {
    setContactsAnchorEl(null)
    setSelectedContact(null)
  }

  const openEditDialogForContact = contact => {
    setSelectedContact(getContactState(customerDetails, contact))
    setIsOpenAddEditDialog(true)
  }

  const toggleDeactivatedListOpen = () => {
    setIsOpenDeactivatedList(s => !s)
  }

  return (
    <>
      {loading && (
        <List disablePadding>
          {shimmersArray.map(() => (
            <ListItem>
              <ListItemIcon>
                <Skeleton variant="circular" width={40} height={40} />
              </ListItemIcon>
              <ListItemText
                primary={<Skeleton variant="text" sx={{ fontSize: '1rem', width: 100 }} />}
                secondary={<Skeleton variant="text" sx={{ fontSize: '1rem', width: 250 }} />}
              />
            </ListItem>
          ))}
        </List>
      )}
      {!loading && activeContacts.length === 0 && deactivatedContacts.length === 0 && (
        <HHSectionPlaceholder
          sx={{
            borderColor: 'info.main',
            borderStyle: 'solid',
            borderWidth: 1,
          }}
          title={noContactMessage}
        />
      )}
      {!loading && activeContacts.length > 0 && (
        <List disablePadding>
          {activeContacts.map((contact, index) => (
            <>
              <ContactView
                contact={contact}
                showActionMenu={showActionMenu}
                bulkExpansion={bulkExpansion}
                onActionClick={event => {
                  setSelectedContact(getContactState(customerDetails, contact))
                  handleOpenActionMenu(event)
                }}
                onRowClick={showActionMenu ? () => openEditDialogForContact(contact) : undefined}
              />
              {(activeContacts.length - 1 !== index || (activeContacts.length - 1 === index && deactivatedContacts.length !== 0)) && (
                <Divider />
              )}
            </>
          ))}
        </List>
      )}
      {!loading && deactivatedContacts.length !== 0 && (
        <>
          <ListItem
            divider
            secondaryAction={
              <Box mr={0.5}>
                <Tooltip arrow title={T.DEACTIVATED_ACCOUNT}>
                  <span>
                    <ContactStatusAvatar isActive={false} />
                  </span>
                </Tooltip>
              </Box>
            }
          >
            <IconButton size="small" onClick={toggleDeactivatedListOpen}>
              {isOpenDeactivatedList ? <ExpandMore /> : <ChevronRight />}
            </IconButton>
            <ListItemText
              primary={T.DISABLED_CONTACTS}
              slotProps={{
                primary: { color: 'textSecondary' },
              }}
            />
          </ListItem>
          <Collapse in={isOpenDeactivatedList}>
            <List disablePadding>
              {deactivatedContacts.map((contact, index) => (
                <>
                  <ContactView
                    contact={contact}
                    showActionMenu={showActionMenu}
                    bulkExpansion={bulkExpansion}
                    onActionClick={event => {
                      setSelectedContact(getContactState(customerDetails, contact))
                      handleOpenActionMenu(event)
                    }}
                    onRowClick={showActionMenu ? () => openEditDialogForContact(contact) : undefined}
                  />
                  {deactivatedContacts.length - 1 !== index && <Divider />}
                </>
              ))}
            </List>
          </Collapse>
        </>
      )}
      <BeneficialOwnersActionMenu
        selectedContact={selectedContact}
        anchorEl={contactsAnchorEl}
        accountId={accountId}
        onEdit={() => {
          setContactsAnchorEl(null)
          setIsOpenAddEditDialog(true)
        }}
        onClose={handleCloseActionMenu}
      />
      {isOpenAddEditDialog && (
        <AddEditBeneficialOwnerDialog
          accountId={accountId}
          contact={selectedContact}
          isOpen={isOpenAddEditDialog}
          onClose={() => {
            setSelectedContact(null)
            setIsOpenAddEditDialog(false)
          }}
        />
      )}
    </>
  )
}

ContactsListing.propTypes = {
  contacts: PropTypes.array,
  loading: PropTypes.bool,
  showActionMenu: PropTypes.bool,
  noContactMessage: PropTypes.string,
  bulkExpansion: PropTypes.shape({
    open: PropTypes.bool,
    version: PropTypes.number,
  }),
}

export default ContactsListing
